import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useReveal from '@/hooks/useReveal';
import { servicesData } from '@/mocks/services';

interface Props {
  slug: string;
}

export default function ServiceDetail({ slug }: Props) {
  useReveal();
  const svc = servicesData.find((s) => s.slug === slug);
  if (!svc) return null;

  return (
    <div className="bg-ink-950 text-white min-h-screen">
      <Navbar />

      <section className="relative pt-[clamp(105px,8.5vw,135px)] pb-12 sm:pb-16 flex items-center overflow-hidden">
        <img src={svc.heroBg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/80 via-ink-950/70 to-ink-950" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 pb-8 w-full">
          <div className="max-w-3xl">
            <div className="text-xs font-mono tracking-widest text-neon-400">// SERVICES</div>
            <h1 className="mt-4 font-display text-3xl sm:text-5xl md:text-6xl font-bold leading-[1.08] tracking-tight text-gradient">
              {svc.title}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
              {svc.fullDesc}
            </p>
          </div>
        </div>
      </section>

      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="mx-auto max-w-7xl grid md:grid-cols-2 gap-10 md:gap-14 items-start">
          <div className="reveal">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">What We Deliver</h2>
            <ul className="mt-5 space-y-3">
              {svc.offerings.map((o) => (
                <li key={o} className="flex items-start gap-3 text-xs sm:text-sm text-white/70">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-neon-500/20 text-neon-400 shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs" />
                  </div>
                  {o}
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <h3 className="font-display text-lg font-bold text-white mb-3">Technologies</h3>
              <div className="flex flex-wrap gap-2">
                {svc.tech.map((t) => (
                  <span key={t} className="px-3 py-1 rounded-full text-xs font-mono bg-white/5 border border-white/10 text-white/70">{t}</span>
                ))}
              </div>
            </div>

            <Link to="/contact" className="btn-primary inline-flex mt-8 text-white font-semibold px-7 py-3.5 rounded-lg text-xs sm:text-sm whitespace-nowrap items-center gap-2">
              Get a Quote <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="reveal relative">
            <div className="absolute -inset-4 rounded-3xl bg-neon-500/20 blur-3xl" />
            <img src={svc.image} alt={svc.title} className="relative rounded-2xl w-full max-h-96 md:max-h-full object-cover border border-white/10" />
          </div>
        </div>
      </section>

      <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="relative mx-auto max-w-4xl text-center reveal">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-gradient">
            Ready to {svc.title.split(' ')[0]}?
          </h2>
          <p className="mt-4 text-white/70 text-base sm:text-lg">Let's discuss your requirements and build something powerful together.</p>
          <Link to="/contact" className="btn-primary inline-flex mt-8 text-white font-semibold px-8 py-3.5 rounded-lg text-xs sm:text-sm whitespace-nowrap items-center gap-2">
            Start Your Project <i className="ri-rocket-2-line" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}